import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Settings2,
  UserPlus,
  Trash2,
  RefreshCw,
  X,
  BadgeCheck,
  Users as UsersIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Modal para gestionar los referentes (inspiration creators). */
export function ReferentesConfigModal({ open, onClose, onChanged }) {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingUsername, setAddingUsername] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/jgmpro/inspiration-creators`, {
        withCredentials: true,
      });
      setCreators(r.data.creators || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const u = addingUsername.trim().replace(/^@/, "");
    if (!u) return;
    setAdding(true);
    try {
      await axios.post(
        `${API}/jgmpro/inspiration-creators`,
        { username: u },
        { withCredentials: true }
      );
      toast.success(`@${u} agregada como referente`);
      setAddingUsername("");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo agregar el referente");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`¿Quitar @${username} de tus referentes?`)) return;
    try {
      await axios.delete(`${API}/jgmpro/inspiration-creators/${username}`, {
        withCredentials: true,
      });
      toast.success(`@${username} eliminada`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleRefresh = async (username) => {
    try {
      toast.info(`Refrescando posts de @${username}…`);
      await axios.post(
        `${API}/jgmpro/inspiration-creators/${username}/refresh`,
        {},
        { withCredentials: true }
      );
      toast.success(`Posts de @${username} actualizados`);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al refrescar");
    }
  };

  const handleRefreshAll = async () => {
    if (
      !window.confirm(
        `¿Refrescar posts de las ${creators.length} referentes? Consume ${creators.length} créditos.`
      )
    )
      return;
    try {
      await axios.post(
        `${API}/jgmpro/inspiration/refresh-all`,
        {},
        { withCredentials: true }
      );
      toast.success("Actualización en background. Datos frescos en 1-3 minutos.");
      setTimeout(() => {
        load();
        onChanged && onChanged();
      }, 15000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al refrescar todas");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="referentes-config-modal"
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-gradient-to-br from-zinc-900 to-zinc-950 border border-fuchsia-500/20 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-zinc-900/95 backdrop-blur p-5 border-b border-white/5 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg font-bold flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-fuchsia-400" />
              Configuración de Referentes
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Modelos de referencia — los videos que ellas publiquen aparecerán en Trending
              para que puedas replicar el contenido ganador.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Add form */}
        <div className="p-5 border-b border-white/5">
          <form onSubmit={handleAdd} className="flex gap-2">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                @
              </span>
              <input
                type="text"
                data-testid="referente-username-input"
                value={addingUsername}
                onChange={(e) =>
                  setAddingUsername(e.target.value.replace(/^@/, "").toLowerCase())
                }
                placeholder="username de la modelo referente (sin @)"
                className="w-full pl-8 pr-3 py-2 rounded-lg bg-zinc-800 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              />
            </div>
            <button
              type="submit"
              disabled={adding || !addingUsername.trim()}
              data-testid="referente-add-btn"
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <UserPlus className="w-4 h-4" />
              Agregar
            </button>
          </form>
          <p className="text-[11px] text-gray-500 mt-2">
            ℹ️ Al agregar, se descargan sus últimos 24 posts (1 crédito). Sus hashtags
            también alimentan el discovery automático.
          </p>
        </div>

        {/* List */}
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-300">
              <UsersIcon className="w-4 h-4 inline mr-1" />
              {creators.length} referentes configuradas
            </div>
            {creators.length > 0 && (
              <button
                onClick={handleRefreshAll}
                data-testid="referentes-refresh-all-btn"
                className="text-xs text-fuchsia-300 hover:text-fuchsia-200 inline-flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                Refrescar todas
              </button>
            )}
          </div>

          {loading ? (
            <div className="py-10 text-center text-gray-500 text-sm">Cargando…</div>
          ) : creators.length === 0 ? (
            <div className="py-10 text-center border border-dashed border-white/10 rounded-xl">
              <div className="text-4xl mb-2">✨</div>
              <p className="text-gray-400 text-sm">
                Aún no tienes referentes. Agrega la primera arriba.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {creators.map((c) => (
                <div
                  key={c.username}
                  data-testid={`referente-row-${c.username}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5"
                >
                  {c.profile_pic_url ? (
                    <img
                      src={`${API}/jgmpro/img-proxy?url=${encodeURIComponent(
                        c.profile_pic_url
                      )}`}
                      alt={c.username}
                      className="w-10 h-10 rounded-full object-cover"
                      onError={(e) => (e.target.style.display = "none")}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                      👤
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-medium flex items-center gap-1">
                      @{c.username}
                      {c.is_verified && (
                        <BadgeCheck className="w-3.5 h-3.5 text-sky-400" />
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {c.followers != null && `${c.followers.toLocaleString()} followers · `}
                      {c.media_count != null && `${c.media_count} posts`}
                      {c.last_error && (
                        <span className="text-red-400 ml-2">⚠ {c.last_error}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRefresh(c.username)}
                    className="p-1.5 rounded hover:bg-white/5 text-gray-400 hover:text-white"
                    title="Refrescar posts (1 crédito)"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(c.username)}
                    data-testid={`referente-delete-${c.username}`}
                    className="p-1.5 rounded hover:bg-red-500/10 text-red-400 hover:text-red-300"
                    title="Quitar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
